import { generateHTML } from '@tiptap/core'

import { jsonConvert } from '../../../helpers/jsonConvert'
import { BlockMath, InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css'
import { getTiptapExtensions } from '@UI/Tiptap/tiptap-extensions'
import { onvertingRelativeURLsToAbsoluteOnes } from './helpers/onvertingRelativeURLsToAbsoluteOnes';

const JsonPars = ({ json }) => {
  const data = jsonConvert(json, 'json')
  if (typeof data === 'string') return <StringHtml text={data} />
  if (typeof data === 'number') return data
  else if (data?.type === undefined) return ''
  return <Html data={data} />
}

const formatString = (text = '') => {
  let newText = `${text}`.replace(/ /g, '\\,').replace(/\n/g, '\\\\'); // заменяем пробелы и переносы на те, которые будут здесь работать

  const mathSymbolsRegex = /([−+=*:<>∩∪≤≥∈∥-])/g; // Обрабатываем +, =, *, -, <, >, ∩, ∪,
  newText = newText.replace(mathSymbolsRegex, '\\text{$1}');

  // Обрабатываем \cap и \cup
  newText = newText.replace(/\\cap/g, '~\\cap~');
  newText = newText.replace(/\\cup/g, '~\\cup~');
  newText = newText.replace(/\\leqslant/g, '~\\leqslant~');
  newText = newText.replace(/\\geqslant/g, '~\\geqslant~');
  newText = newText.replace(/\\parallel/g, '~\\parallel~');
  newText = newText.replace(/\\notin/g, '~\\notin~');
  newText = newText.replace(/\\in(?!\w)/g, '~\\in~');
  newText = newText.replace(/\\ge(?!\w)/g, '~\\ge~');
  newText = newText.replace(/\\le(?!\w)/g, '~\\le~');
  newText = newText.replace(/\\neq(?!\w)/g, '~\\neq~');
  newText = newText.replace(/\\equiv(?!\w)/g, '~\\equiv~');

  newText = newText.replace(/\\cdot/g, '~\\cdot~');
  newText = newText.replace(/%/g, '\\%');

  return newText;
};

const getMathNodeValue = (node) => {
  if (typeof node?.attrs?.value === 'string' && node.attrs.value.length > 0) {
    return node.attrs.value;
  }

  if (typeof node?.text === 'string') {
    return node.text;
  }

  const content = Array.isArray(node?.content)
    ? node.content
    : node?.content?.content;

  if (Array.isArray(content)) {
    return content.map((child) => getMathNodeValue(child)).join('');
  }

  return '';
};

const InlineFormula = ({ value }) => (
  <span className="tiptapInlineMath">
    <InlineMath>{formatString(value ?? '')}</InlineMath>
  </span>
);

const renderInlineString = (text, keyPrefix) => {
  const nodes = [];
  const inlineLatexRegex = /\$([^$\n]+?)\$|\\\(([\s\S]+?)\\\)/g;
  let lastIndex = 0;
  let match = inlineLatexRegex.exec(text);

  while (match) {
    const textBeforeLatex = text.slice(lastIndex, match.index);

    if (textBeforeLatex) {
      nodes.push(textBeforeLatex);
    }

    nodes.push(<InlineFormula key={`${keyPrefix}-math-${match.index}`} value={match[1] ?? match[2]} />);
    lastIndex = match.index + match[0].length;
    match = inlineLatexRegex.exec(text);
  }

  const textAfterLatex = text.slice(lastIndex);
  if (textAfterLatex) {
    nodes.push(textAfterLatex);
  }

  return nodes;
};

const renderTextLines = (text, keyPrefix) => (
  text.split(/\r?\n/).flatMap((line, index, lines) => {
    const lineNodes = renderInlineString(line, `${keyPrefix}-${index}`);
    return index < lines.length - 1
      ? [...lineNodes, <br key={`${keyPrefix}-br-${index}`} />]
      : lineNodes;
  })
);

const StringHtml = ({ text }) => {
  const content = [];
  const blockLatexRegex = /\$\$([\s\S]+?)\$\$|\\\[([\s\S]+?)\\\]/g;
  let lastIndex = 0;
  let match = blockLatexRegex.exec(text);

  while (match) {
    const textBeforeLatex = text.slice(lastIndex, match.index);

    if (textBeforeLatex) {
      content.push(...renderTextLines(textBeforeLatex, `text-${lastIndex}`));
    }

    content.push(
      <BlockMath key={`block-math-${match.index}`}>
        {formatString(match[1] ?? match[2] ?? '')}
      </BlockMath>,
    );
    lastIndex = match.index + match[0].length;
    match = blockLatexRegex.exec(text);
  }

  const textAfterLatex = text.slice(lastIndex);
  if (textAfterLatex) {
    content.push(...renderTextLines(textAfterLatex, `text-${lastIndex}`));
  }

  return <span>{content}</span>;
};

const Html = ({ data }) => {
  const content = onvertingRelativeURLsToAbsoluteOnes(data?.content) ?? [];
  const renderGeneratedInlineNode = (node, key) => {
    const html = generateHTML(
      { type: 'doc', content: [{ type: 'paragraph', content: [node] }] },
      getTiptapExtensions(),
    ).replace(/^<p>/, '').replace(/<\/p>$/, '');

    return (
      <span
        key={key}
        dangerouslySetInnerHTML={{
          __html: html,
        }}
      />
    );
  };

  const renderInlineContent = (nodes = []) => (
    nodes.map((node, index) => {
      if (node?.type === 'inlineMath' || node?.type === 'math') {
        return (
          <span className="tiptapInlineMath" key={index}>
            <InlineMath>{formatString(getMathNodeValue(node))}</InlineMath>
          </span>
        );
      }

      if (node?.type === 'hardBreak') {
        return <br key={index} />;
      }

      return renderGeneratedInlineNode(node, index);
    })
  );

  const hasInlineMath = (nodes = []) => (
    nodes.some((node) => node?.type === 'inlineMath' || node?.type === 'math')
  );

  const renderBlockWithMath = (item, key) => {
    const style = item?.attrs?.textAlign
      ? { textAlign: item.attrs.textAlign }
      : undefined;

    if (item?.type === 'heading') {
      const level = Number(item?.attrs?.level);
      const HeadingTag = `h${level >= 1 && level <= 6 ? level : 1}`;

      return (
        <div className='tiptapStyle' key={key}>
          <HeadingTag style={style}>
            {renderInlineContent(item?.content)}
          </HeadingTag>
        </div>
      );
    }

    return (
      <p className='tiptapStyle' key={key} style={style}>
        {renderInlineContent(item?.content)}
      </p>
    );
  };

  return <>
    {content?.map((item, i) => {
      if (item?.type === 'math') {
        const formattedText = formatString(getMathNodeValue(item));
        return <BlockMath key={i}>{formattedText}</BlockMath>
      }
      if (
        ['paragraph', 'heading'].includes(item?.type) &&
        hasInlineMath(item?.content)
      ) {
        return renderBlockWithMath(item, i);
      }
      return (
        <span 
          className='tiptapStyle' 
          key={i} 
          dangerouslySetInnerHTML={{ 
            __html: generateHTML(
              { type: 'doc', content: [item] }, 
              getTiptapExtensions()
          ) }} />
      )
    })}
  </>
}

export default JsonPars;
